import { FC } from 'react'
import { Skeleton } from 'src/components/Skeleton'
import { AiListeningDateRange } from 'src/hooks/useAiListeningDateRange'
import { useGetAiListeningProfileDashboardQuery } from 'src/reactQuery/useGetAiListeningProfileDashboardQuery'
import { KpiTiles } from './KpiTiles'
import { MentionsCard, ReachCard } from './ReachMentions'
import { ScoreCard } from './ScoreCard'
import {
  CitedPagesCard,
  CompetitorsDomainsCard,
  PlatformsCard,
  TopicsCard,
} from './Tables'
import styles from './dashboard.module.scss'
import { StateCard } from '../StateCard'

interface BrandDashboardViewProps {
  // The brand profile id; on a competitor's dashboard its row is marked as
  // the user's own brand.
  profileId: string
  range?: AiListeningDateRange
  // The brand profile itself, or one of its competitors.
  subjectId: string
}

const DashboardSkeleton: FC = () => (
  <div className={styles.dashboard}>
    <div className={styles.card}>
      <Skeleton height={340} radius={16} width="100%" />
    </div>
    <div className={styles.kpis}>
      {Array.from({ length: 5 }, (_, index) => (
        <div className={styles.kpi} key={index}>
          <Skeleton height={14} width="60%" />
          <Skeleton height={28} width="40%" />
        </div>
      ))}
    </div>
    <div className={styles.reachMentionsRow}>
      <div className={styles.card}>
        <Skeleton height={300} radius={16} width="100%" />
      </div>
      <div className={styles.card}>
        <Skeleton height={300} radius={16} width="100%" />
      </div>
    </div>
  </div>
)

// The date-ranged dashboard of one subject (brand or competitor): score,
// KPI tiles, reach, mentions, platforms, topics, competitors, domains, pages.
export const BrandDashboardView: FC<BrandDashboardViewProps> = ({
  profileId,
  range,
  subjectId,
}) => {
  const { data, isError, isLoading, refetch } =
    useGetAiListeningProfileDashboardQuery({
      from: range?.from,
      profileId,
      subject: subjectId,
      to: range?.to,
    })

  if (isLoading) return <DashboardSkeleton />

  if (isError || !data) {
    return (
      <StateCard
        action={{ label: 'Try again', onClick: () => refetch() }}
        description="The dashboard could not be loaded. Check your connection and try again."
        icon="processing"
        title="Something went wrong"
      />
    )
  }

  if (!data.hasData) {
    return (
      <StateCard
        description="None of the AI answers to your tracked prompts fall in the selected period. Pick a longer period in the date filter, or check back after the next weekly run."
        icon="processing"
        title="No AI answers in this period yet"
      />
    )
  }

  return (
    <div className={styles.dashboard}>
      <ScoreCard score={data.score} />
      <KpiTiles kpis={data.kpis} />
      <div className={styles.reachMentionsRow}>
        <ReachCard brandName={data.subject.brandName} reach={data.reach} />
        <MentionsCard mentions={data.mentions} period={data.period} />
      </div>
      <div className={styles.twoColumns}>
        <PlatformsCard
          needingAttention={data.platformsNeedingAttention}
          platforms={data.platforms}
        />
        <TopicsCard topics={data.topics} />
      </div>
      <CompetitorsDomainsCard
        brandProfileId={profileId}
        competitors={data.competitors}
        domains={data.domains}
        isCompetitorView={subjectId !== profileId}
        ownedCitationShare={data.ownedCitationShare}
      />
      <CitedPagesCard
        ownedPagesInTop={data.ownedPagesInTop}
        pages={data.pages}
      />
    </div>
  )
}
